import { motion } from 'motion/react';
import { Heart } from 'lucide-react';
import type { Model3D } from '@visionar/shared';
import { useModelsStore } from '@/stores/models-store';
import { useUIStore } from '@/stores/ui-store';
import { useAuthStore } from '@/stores/auth-store';
import { useNotificationsStore } from '@/stores/notifications-store';

interface ModelCardProps {
  model: Model3D;
  size?: 'sm' | 'md';
}

export default function ModelCard({ model, size = 'md' }: ModelCardProps) {
  const { isFavorite, toggleFavorite } = useModelsStore();
  const { setSelectedModel } = useUIStore();
  const { isAuthenticated } = useAuthStore();
  const { notifyFavoriteAdded, notifyFavoriteRemoved } = useNotificationsStore();
  const isFav = isFavorite(model.id);

  const handleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAuthenticated) return;
    const wasFav = isFav;
    try {
      await toggleFavorite(model.id);
      if (wasFav) {
        notifyFavoriteRemoved(model.name);
      } else {
        notifyFavoriteAdded(model.name, model.id, model.thumbnailUrl);
      }
    } catch {
      // Error handled by store
    }
  };

  const handleClick = () => {
    setSelectedModel(model);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  if (size === 'sm') {
    return (
      <motion.div
        whileTap={{ scale: 0.96 }}
        onClick={handleClick}
        className="glass rounded-3xl p-3 cursor-pointer group"
      >
        <div className="relative h-36 rounded-2xl overflow-hidden mb-3">
          <img
            src={model.thumbnailUrl}
            alt={model.name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
          />
          {isAuthenticated && (
            <button
              onClick={handleFavorite}
              className="absolute top-2 right-2 w-8 h-8 rounded-full glass-dark flex items-center justify-center transition-transform hover:scale-110"
            >
              <Heart
                size={14}
                className={isFav ? 'text-red-500 fill-red-500' : 'text-white/70'}
              />
            </button>
          )}
        </div>
        <p className="text-white/50 text-[10px] font-bold uppercase tracking-wider mb-1">
          {model.brand?.name}
        </p>
        <h4 className="font-medium text-sm mb-1 truncate">{model.name}</h4>
        <p className="text-accent-blue text-sm font-semibold">
          {formatPrice(model.price)}
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      whileTap={{ scale: 0.98 }}
      onClick={handleClick}
      className="glass rounded-3xl p-4 cursor-pointer group"
    >
      <div className="relative h-48 rounded-2xl overflow-hidden mb-4">
        <img
          src={model.thumbnailUrl}
          alt={model.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        {model.isPremium && (
          <div className="absolute top-3 left-3 bg-accent-purple/90 backdrop-blur-sm px-2 py-1 rounded-full">
            <span className="text-[10px] font-bold uppercase">Premium</span>
          </div>
        )}
        {isAuthenticated && (
          <button
            onClick={handleFavorite}
            className="absolute top-3 right-3 w-10 h-10 rounded-full glass-dark flex items-center justify-center transition-transform hover:scale-110"
          >
            <Heart
              size={18}
              className={isFav ? 'text-red-500 fill-red-500' : 'text-white/70'}
            />
          </button>
        )}
      </div>
      <div className="flex justify-between items-start">
        <div>
          <p className="text-white/50 text-xs font-bold uppercase tracking-wider mb-1">
            {model.brand?.name}
          </p>
          <h4 className="font-semibold text-lg mb-1">{model.name}</h4>
          <p className="text-white/50 text-sm line-clamp-2">{model.description}</p>
        </div>
        <p className="text-accent-blue font-bold text-lg">
          {formatPrice(model.price)}
        </p>
      </div>
    </motion.div>
  );
}
